import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getActivityLogs, getUsers } from '../services/api';
import './ActivityLogModal.css';

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString();
  } catch {
    return String(iso);
  }
}

function ActivityLogModal({ isOpen, onClose }) {
  const { user } = useAuth();
  const admin = user?.role === 'admin';
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(25);
  const [userFilter, setUserFilter] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [missingTable, setMissingTable] = useState(false);

  const load = useCallback(async (pageNum) => {
    setLoading(true);
    setError(null);
    setMissingTable(false);
    try {
      const data = await getActivityLogs({
        page: pageNum,
        perPage,
        userId: userFilter || undefined
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
      if (data.page) setPage(data.page);
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'activity_table_missing' || err.response?.status === 503) {
        setMissingTable(true);
        setItems([]);
        setTotal(0);
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to load activity');
      }
    } finally {
      setLoading(false);
    }
  }, [perPage, userFilter]);

  useEffect(() => {
    if (isOpen && admin) {
      getUsers()
        .then(setUsers)
        .catch(() => setUsers([]));
    }
  }, [isOpen, admin]);

  useEffect(() => {
    if (isOpen && admin) {
      load(1);
    }
  }, [isOpen, admin, userFilter, load]);

  if (!admin || !isOpen) {
    return null;
  }

  const totalPages = Math.max(1, Math.ceil(total / perPage) || 1);

  return (
    <div className="activity-log-overlay" onClick={onClose}>
      <div className="activity-log-modal" onClick={(e) => e.stopPropagation()}>
        <div className="activity-log-header">
          <h2>User activity</h2>
          <button type="button" className="close-button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="activity-log-toolbar">
          <label>
            <span>Filter by user</span>
            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
            >
              <option value="">All users</option>
              {users.map((u) => (
                <option key={u.id} value={String(u.id)}>
                  {u.username} ({u.role})
                </option>
              ))}
            </select>
          </label>
          <span className="activity-log-count">{total} event(s)</span>
        </div>

        {missingTable && (
          <div className="activity-log-banner">
            The activity log table is not created yet. On the server, run the SQL in{' '}
            <code>backend_python/migrations/add_activity_logs.sql</code>, then restart the API.
          </div>
        )}
        {error && <div className="activity-log-error">{error}</div>}

        <div className="activity-log-table-wrap">
          {loading && items.length === 0 ? (
            <div className="activity-log-loading">Loading…</div>
          ) : (
            <table className="activity-log-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Summary</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id}>
                    <td className="cell-time">{formatWhen(row.created_at)}</td>
                    <td>{row.username || (row.user_id ? `#${row.user_id}` : '—')}</td>
                    <td><code className="cell-action">{row.action}</code></td>
                    <td className="cell-summary">{row.summary}</td>
                    <td className="cell-ip">{row.ip_address || '—'}</td>
                  </tr>
                ))}
                {!loading && items.length === 0 && !missingTable && (
                  <tr>
                    <td colSpan={5} className="cell-empty">No activity recorded yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="activity-log-pagination">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => load(page - 1)}
            >
              Previous
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages || loading}
              onClick={() => load(page + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ActivityLogModal;
